// @ts-nocheck
import { Autocomplete, Box, IconButton, MenuItem, Tooltip, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import { AppTextField } from '../AppTextField'
import { invalidateCustomer, useApproveEquipmentCustomers, useGetCustomersForEquipmentApproval } from 'src/api/customer'
import { useTranslation } from 'react-i18next'
import { GenericDialogLayout } from '../GenericDialogLayout'
import { MainButton } from '../MainButton'
import { ShowDialogProps } from 'src/show-dialog'
import { ICustomer } from 'src/generated-types'
import { GridColumns, GridNativeColTypes } from '@mui/x-data-grid-pro'
import { MdDeleteOutline } from 'react-icons/md'
import GenericTable from '../table/GenericTable'
import { Spinner } from '../Spinner'
import { EmptyStatusCard } from '../mooring/EmptyStatusCard'
import { toast } from 'react-toastify'

export interface ApprovedEquipmentCustomersDialogProps extends ShowDialogProps {
  customerId: number
}

const ApprovedEquipmentCustomersDialog: React.FC<ApprovedEquipmentCustomersDialogProps> = ({ customerId, pop }) => {
  const [search, setSearch] = useState('')
  const [customerError, setCustomerError] = useState(false)
  const [selectedCustomers, setSelectedCustomers] = useState<ICustomer[]>([])
  const { data: customersList, isLoading: isLoadingCustomers } = useGetCustomersForEquipmentApproval(
    customerId as EntityId,
  )
  const { mutate: approveCustomers, isLoading: isApproving } = useApproveEquipmentCustomers(customerId as EntityId)

  const { t: translate } = useTranslation(['main-component', 'users', 'document-category', 'common'])

  const handleAdd = () => {
    approveCustomers(
      { approvedCustomerIds: selectedCustomers.map(c => c.id as number) },
      {
        onSuccess: () => {
          invalidateCustomer.getApprovedEquipmentCustomers(customerId as EntityId)
          invalidateCustomer.getCustomersForEquipmentApproval(customerId as EntityId)
          toast.success(translate('customersApprovedSuccessfully', { ns: 'main-component' }))
          pop()
        },
      },
    )
  }

  const onDeleteSelectedCustomer = (id: EntityId) => {
    setSelectedCustomers(prev => prev.filter(c => c.id !== id))
  }
  const STATIC_COLUMNS: GridColumns = [
    { field: 'name', headerName: translate('name', { ns: 'common' }), flex: 1 },
    { field: 'address', headerName: translate('address', { ns: 'common' }), flex: 1 },
    { field: 'city', headerName: translate('city', { ns: 'common' }), flex: 1 },
    { field: 'country', headerName: translate('country', { ns: 'common' }), flex: 1 },
  ]

  const columns: GridColumns = useMemo(
    () => [
      ...STATIC_COLUMNS,

      {
        field: 'actions',
        type: 'actions' as GridNativeColTypes,
        width: 100,
        renderCell: ({ row }) => [
          <Tooltip title={`${translate('delete', { ns: 'common' })}`} key={'delete'}>
            <IconButton onClick={() => onDeleteSelectedCustomer(row.id)} color="primary">
              <MdDeleteOutline size={24} />
            </IconButton>
          </Tooltip>,
        ],
      },
    ],
    [],
  )

  return (
    <GenericDialogLayout
      title={translate('approve-customers', { ns: 'main-component' })}
      pop={pop}
      actions={[
        <MainButton
          key="submit"
          onClick={() => {
            handleAdd()
          }}
        >
          {isApproving && <Spinner size={20} />}
          {translate('approve', { ns: 'document-category' })}
        </MainButton>,
        <MainButton key="cancel" onClick={pop} variant="outlined">
          {translate('cancel', { ns: 'common' })}
        </MainButton>,
      ]}
    >
      <Box>
        <Autocomplete
          options={customersList || []}
          onChange={(_, value) => {
            setCustomerError(false)
            setSelectedCustomers(prev => {
              if (value && !prev.find(c => c.id === value.id)) {
                return [...prev, value]
              }
              return prev
            })
          }}
          value={null}
          getOptionLabel={o => o.name}
          loading={isLoadingCustomers}
          renderOption={(props, o) => (
            <MenuItem key={o.id} value={o.id} {...props}>
              {o.name}
            </MenuItem>
          )}
          renderInput={params => (
            <AppTextField
              {...params}
              label={translate('customers', { ns: 'users' })}
              inputProps={{
                ...params.inputProps,
              }}
              onChange={e => {
                setSearch(e.target.value)
              }}
              placeholder={search || translate('customer-select', { ns: 'main-component' })}
            />
          )}
          inputValue={search}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          clearIcon={null}
        />
        {customerError && (
          <Typography color="error" variant="body2" sx={{ paddingLeft: '8px', paddingBottom: '12px' }}>
            {translate('select-valid-customer', { ns: 'main-component' })}
          </Typography>
        )}
      </Box>
      <Box padding={1}>
        <GenericTable
          id="customers"
          data={selectedCustomers}
          columns={columns}
          getRowHeight={() => 44}
          noDataComponent={() => {
            return (
              <EmptyStatusCard
                imageWidth="60px"
                header={translate('selected-customer-empty-status-title', { ns: 'main-component' })}
                content={translate('selected-customer-empty-status-content', { ns: 'main-component' })}
                headerFontSize="14px"
                contentFontSize="12px"
                contentClasses="mt-2"
                contentTopMargin="10px"
                imageContainerClasses="pb-1 pt-1"
                height="100%"
              />
            )
          }}
        />
      </Box>
    </GenericDialogLayout>
  )
}

export default ApprovedEquipmentCustomersDialog
